import React, { useMemo, useState } from 'react';
import { Card, CardContent, Collapse, Grid, IconButton, Typography, useTheme } from '@mui/material';
import { InfoTooltip } from '../../../HOC/Tooltips';
import { COLOR_TOOLTIP_BACKGROUND } from 'src/const';
import {
  COLOR_PRIMARY_1,
  COLOR_PRIMARY_1_700,
  COLOR_PRIMARY_2,
  COLOR_SECONDARY_2,
  COLOR_SECONDARY_2_600,
  COLOR_TEXT_2,
} from 'src/colors';
import {
  getHeadingStyle,
  getPercentage,
  getPreviewTextObj,
  getSupplyByPropertySize,
} from 'src/utils/functions';
import { infoIconButton, infoIconStyle } from 'src/App/Dashboards/Market/styles/style';
import { GraphPreview } from 'src/App/Components';
import { DynamicBarChart } from 'src/App/Components/Charts';
import { ViewDetailsButton } from 'src/App/Components/Buttons';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { FilterVal, VisibleListingsProp } from 'src/interfaces/iproptypes';
import { usePreview } from 'src/utils/hooks';
import { LoadingCard } from 'src/App/HOC';
import NoDataCard from 'src/App/HOC/Cards/NoDataCard';
import ShowMoreButton from 'src/App/Components/Buttons/ShowMoreButton';

const PropertySizeCard = ({
  listings,
  filterVal,
  cachedData,
  viewMode,
  isDynamicLoading,
  multipleMarkersCallLoading,
  noDataInMarket,
  totalListingsCount,
}: {
  listings: VisibleListingsProp;
  filterVal: FilterVal;
  cachedData: { [key: string]: number } | null | undefined;
  viewMode: 'map' | 'dashboard' | 'discovery' | 'compare' | 'advance' | 'searchListing';
  isDynamicLoading: boolean;
  multipleMarkersCallLoading: Boolean;
  noDataInMarket: boolean;
  totalListingsCount: number;
}) => {
  const theme = useTheme();

  const [isTooltipOpen, setIsTooltipOpen] = useState(false);

  const [isPropertySizePreview, handlePropertySizePreviewMode] = usePreview() as [
    boolean,
    () => void
  ];

  const propertySizePreviewTextObj = getPreviewTextObj(
    'supply',
    'propertySize',
    filterVal,
    listings.data
  );

  const isDataStatic = viewMode === 'dashboard' && cachedData;
  const isDataLoading = multipleMarkersCallLoading && !listings.data?.length && !isDataStatic;

  const supplyByPropertySize = useMemo(() => {
    if (isDataLoading) {
      return [];
    }
    return getSupplyByPropertySize(listings.data, isDataStatic ? cachedData : null);
  }, [listings, cachedData, viewMode]);

  if (isDataLoading || isDynamicLoading) {
    return <LoadingCard height={150} type="bars" />;
  }

  if (noDataInMarket) {
    return <NoDataCard height={150} borderRadius={8} />;
  }

  const getTooltipSuffix = (value: number, key: string) => {
    const percentageOfTotalListings = Math.round((value / Math.max(1, totalListingsCount)) * 100);
    return `${value} listings ( ${
      percentageOfTotalListings ? percentageOfTotalListings + '% ' : 'less than 1% '
    } )`;
  };

  return (
    <Grid
      container
      direction="column"
      justifyContent="center"
      item
      xs={12}
      sx={{ boxShadow: '0 4px 10px #0003', borderRadius: 1, ...theme.utils.cardPadding }}
    >
      <Grid container item sx={{ width: '100%' }}>
        <Typography variant="body1" sx={getHeadingStyle(COLOR_TEXT_2)}>
          Number of Listings by Property Size
          <InfoTooltip
            enterTouchDelay={0}
            leaveTouchDelay={30000}
            title={
              <Card>
                <CardContent style={{ background: COLOR_TOOLTIP_BACKGROUND }}>
                  <Typography variant="body2" style={{ fontWeight: 500 }}>
                    This data only includes entire place listings on Airbnb.
                  </Typography>
                </CardContent>
              </Card>
            }
            style={{ cursor: 'pointer' }}
          >
            <IconButton style={infoIconButton} onClick={() => setIsTooltipOpen(prev => !prev)}>
              <InfoOutlinedIcon style={infoIconStyle} />
            </IconButton>
          </InfoTooltip>
        </Typography>

        <DynamicBarChart
          layout="horizontal"
          data={supplyByPropertySize}
          unit="listings"
          label="Bedrooms"
          height={150}
          barColor={COLOR_PRIMARY_1_700}
          barAccentColor={COLOR_PRIMARY_1}
          useShortHand={true}
          isPropertySizeGraph={true}
          getTooltipText={getTooltipSuffix}
          parent="listing_by_property_size"
        />

        <Grid container item flexDirection={'row-reverse'} xs={12}>
          <ShowMoreButton
            isShowingLess={!isPropertySizePreview}
            handleButtonClick={handlePropertySizePreviewMode}
          />
        </Grid>

        <Collapse in={isPropertySizePreview}>
          <GraphPreview
            parent="supply"
            variant="propertySize"
            useFriendlyDataName={propertySizePreviewTextObj.userFriendlyDataName}
            previewVal={propertySizePreviewTextObj.previewVal}
          />
        </Collapse>
      </Grid>
    </Grid>
  );
};

export default React.memo(PropertySizeCard);
